import { useEffect, useState, useRef, useCallback } from "react";

const fontStyles = `
  @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@300;500;700;900&family=Montserrat:wght@200;400;700&display=swap');
`;

const ElevatorAnimation = ({ onAnimationComplete }) => {
  const [doorState, setDoorState] = useState("closed");
  const [floorCount, setFloorCount] = useState(1);
  const [isStarted, setIsStarted] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [lightsOn, setLightsOn] = useState(false);
  const [responsiveStyles, setResponsiveStyles] = useState({
    smallScreen: window.innerWidth <= 820,
  });

  // Particle animation for the start screen
  const [particles, setParticles] = useState([]);
  const particleRef = useRef(null);
  const audioRef = useRef(null);
  const doorSoundRef = useRef(null);
  const containerRef = useRef(null);

  // Add refs to safely manage animations
  const animationFrameRef = useRef(null);
  const isMountedRef = useRef(true);

  // Generate particles for the intro screen
  useEffect(() => {
    if (!isStarted && particleRef.current) {
      // Use safe dimensions
      const containerWidth =
        particleRef.current?.offsetWidth || window.innerWidth;
      const containerHeight =
        particleRef.current?.offsetHeight || window.innerHeight;

      const newParticles = [];
      for (let i = 0; i < 30; i++) {
        newParticles.push({
          x: Math.random() * containerWidth,
          y: Math.random() * containerHeight,
          size: Math.random() * 3 + 1,
          speedX: Math.random() * 1 - 0.5,
          speedY: Math.random() * 1 - 0.5,
          opacity: Math.random() * 0.5 + 0.3,
        });
      }
      setParticles(newParticles);

      const animateParticles = () => {
        // Safe check for mounted state and ref existence
        if (!isMountedRef.current || !particleRef.current) return;

        setParticles((prevParticles) =>
          prevParticles.map((p) => {
            // Get dimensions safely
            const width = particleRef.current?.offsetWidth || window.innerWidth;
            const height =
              particleRef.current?.offsetHeight || window.innerHeight;

            return {
              ...p,
              x: (p.x + p.speedX + width) % width,
              y: (p.y + p.speedY + height) % height,
            };
          })
        );

        if (!isStarted && isMountedRef.current) {
          animationFrameRef.current = requestAnimationFrame(animateParticles);
        }
      };

      animationFrameRef.current = requestAnimationFrame(animateParticles);
    }

    // Proper cleanup
    return () => {
      isMountedRef.current = false;
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [isStarted]);

  // Run animation sequence after user interaction
  const startAnimation = () => {
    setIsStarted(true);
    setIsAnimating(true);

    // Create a user interaction context for audio
    const unlockAudio = () => {
      // Create context for audio playback
      const audioContext = new (window.AudioContext ||
        window.webkitAudioContext)();

      // Resume the audio context
      if (audioContext.state === "suspended") {
        audioContext.resume();
      }

      // Pre-load the audio files
      if (audioRef.current) {
        audioRef.current.load();
      }
      if (doorSoundRef.current) {
        doorSoundRef.current.load();
      }

      // Create and play a silent sound to unlock audio
      const silentSound = new Audio(
        "data:audio/mp3;base64,SUQzBAAAAAAAI1RTU0UAAAAPAAADTGF2ZjU4LjI5LjEwMAAAAAAAAAAAAAAA//tQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAWGluZwAAAA8AAAACAAABQABgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBgYGBg//8AAAAATGF2YzU4LjU0AAAAAAAAAAAAAAAAJAAAAAAAAAAAQOsDn1xxAAAAAAAAAAAAAAAAAAAAAP/7kGQAAAAAADkAAAAIAAANIAAAAQAAAOQAAAAgAAA0gAAABExBTUUzLjEwMFVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVV"
      );

      // Play with both methods to maximize compatibility
      const playPromise = silentSound.play();

      if (playPromise !== undefined) {
        playPromise.catch((error) => {
          console.log(
            "Silent sound failed, but we'll try again with the real sounds"
          );
        });
      }
    };

    unlockAudio();
  };

  const playSound = useCallback((audioElement) => {
    if (!audioElement) return;

    // Set volume to ensure it's audible
    audioElement.volume = 1.0;

    // Try multiple play methods for maximum browser compatibility
    const playPromise = audioElement.play();

    if (playPromise !== undefined) {
      playPromise.catch((error) => {
        // If autoplay was prevented, try again with user interaction
        console.log("Sound play error:", error);
      });
    }
  }, []);

  // Enhanced keyframes styles for more professional animations
  const keyframesStyle = `
        @keyframes fadeInUp {
            from {
                opacity: 0;
                transform: translateY(40px);
            }
            to {
                opacity: 1;
                transform: translateY(0);
            }
        }
        
        @keyframes digitalPulse {
            0% {
                text-shadow: 0 0 5px rgba(0, 123, 255, 0.3), 0 0 10px rgba(0, 123, 255, 0.1);
                color: rgba(0, 123, 255, 0.9);
            }
            50% {
                text-shadow: 0 0 15px rgba(0, 123, 255, 0.7), 0 0 25px rgba(0, 123, 255, 0.3);
                color: rgba(0, 123, 255, 1);
            }
            100% {
                text-shadow: 0 0 5px rgba(0, 123, 255, 0.3), 0 0 10px rgba(0, 123, 255, 0.1);
                color: rgba(0, 123, 255, 0.9);
            }
        }
        
        @keyframes buttonGlow {
            0% {
                box-shadow: 0 0 5px rgba(0, 123, 255, 0.5);
            }
            50% {
                box-shadow: 0 0 15px rgba(0, 123, 255, 0.8), 0 0 20px rgba(0, 123, 255, 0.4);
            }
            100% {
                box-shadow: 0 0 5px rgba(0, 123, 255, 0.5);
            }
        }
        
        @keyframes dataStream {
            0% {
                background-position: 0% 0%;
            }
            100% {
                background-position: 0% 100%;
            }
        }
        
        @keyframes elevatorHum {
            0%, 100% {
                transform: translateY(0);
            }
            50% {
                transform: translateY(1px);
            }
        }
        
        @keyframes floatUp {
            0%, 100% {
                transform: translateY(0) translateZ(30px);
            }
            50% {
                transform: translateY(-5px) translateZ(30px);
            }
        }

        @keyframes gridBackground {
            0% {
                background-position: 0px 0px;
            }
            100% {
                background-position: 50px 50px;
            }
        }
        
        @keyframes scanline {
            0% {
                transform: translateY(-100%);
                opacity: 0.1;
            }
            50% {
                opacity: 0.3;
            }
            100% {
                transform: translateY(100%);
                opacity: 0.1;
            }
        }

        @keyframes floorNumberChange {
            0% {
                transform: scale(1.2);
                opacity: 0.7;
            }
            100% {
                transform: scale(1);
                opacity: 1;
            }
        }

        @keyframes doorShine {
            0% {
                background-position: -100% 0;
            }
            100% {
                background-position: 200% 0;
            }
        }

        @keyframes panelGlow {
            0% {
                opacity: 0.4;
            }
            50% {
                opacity: 0.9;
            }
            100% {
                opacity: 0.4;
            }
        }
        
        @keyframes cityLights {
            0% {
                background-position: 0% 0%;
            }
            100% {
                background-position: 100% 100%;
            }
        }
        
        .welcome-heading {
            animation: fadeInUp 0.8s ease-out forwards;
        }
        
        .welcome-text {
            animation: fadeInUp 0.8s ease-out 0.2s forwards;
            opacity: 0;
        }
        
        .start-button {
            animation: fadeInUp 0.8s ease-out 0.4s forwards, buttonGlow 3s infinite 1s;
            opacity: 0;
        }
        
        .logo-container {
            animation: fadeInUp 1.2s cubic-bezier(0.23, 1, 0.32, 1) forwards, floatUp 4s ease-in-out infinite 1.2s;
        }
        
        .digital-text {
            animation: digitalPulse 3s infinite;
        }
        
        .floor-button.active {
            animation: buttonGlow 1.5s infinite;
        }
        
        .elevator-interior {
            animation: elevatorHum 3s ease-in-out infinite;
        }
        
        .welcome-container {
            background: linear-gradient(135deg, #0a1930 0%, #162b50 100%);
            background-size: 200% 200%;
        }
        
        .floor-number {
            animation: floorNumberChange 0.3s ease-out;
        }

        @keyframes blinkingLight {
          0%, 100% {
            opacity: 0.3;
          }
          50% {
            opacity: 1;
          }
        }

        @keyframes digitalFlicker {
          0%, 5%, 10%, 15%, 100% {
            opacity: 1;
          }
          7%, 12% {
            opacity: 0.8;
          }
        }

        @keyframes floorNumberPulse {
          0%, 100% {
            text-shadow: 0 0 8px rgba(0, 123, 255, 0.6);
            transform: scale(1);
          }
          50% {
            text-shadow: 0 0 15px rgba(0, 123, 255, 1), 0 0 30px rgba(0, 123, 255, 0.5);
            transform: scale(1.02);
          }
        }

        @keyframes starTwinkle {
          0%, 100% {
            opacity: 0.4;
          }
          50% {
            opacity: 0.7;
          }
        }

        .floor-number {
          animation: floorNumberChange 0.3s ease-out, floorNumberPulse 3s infinite ease-in-out;
        }

        .stars {
          animation: starTwinkle 4s infinite ease-in-out;
        }
    `;

  // Modify the animation sequence function in the useEffect
  useEffect(() => {
    // Only run animation if started by user
    const handleResize = () => {
      // Update responsive styles based on window size
      setResponsiveStyles({
        smallScreen: window.innerWidth <= 820,
      });
    };

    handleResize();
    window.addEventListener("resize", handleResize);

    if (!isStarted) return;

    const sequence = async () => {
      try {
        // Turn on elevator lights
        setLightsOn(true);

        // Initial floor counting animation
        for (let i = 1; i <= 5; i++) {
          setFloorCount(i);
          await new Promise((resolve) => setTimeout(resolve, 200));
        }

        // Play door opening sound with new function
        if (doorSoundRef.current) {
          playSound(doorSoundRef.current);
        }

        // Start opening doors
        setDoorState("opening");

        // Doors fully open
        await new Promise((resolve) => setTimeout(resolve, 1500));
        setDoorState("open");

        // Play elevator ding sound
        if (audioRef.current) {
          playSound(audioRef.current);
        }

        // Short pause to appreciate the open doors, then transition to home page
        await new Promise((resolve) => setTimeout(resolve, 300));

        // Transition to home page
        if (containerRef.current) {
          containerRef.current.style.opacity = 0;
        }
        setTimeout(() => {
          onAnimationComplete();
        }, 600);
      } catch (error) {
        console.error("Animation sequence error:", error);
        onAnimationComplete();
      }
    };

    sequence();

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [isStarted, onAnimationComplete, playSound]);

  return (
    <>
      {/* Inject keyframes */}
      <style>{keyframesStyle}</style>
      <style>{fontStyles}</style>

      <div ref={containerRef} id="elevator-container" style={styles.container}>
        {!isStarted ? (
          <div
            ref={particleRef}
            className="welcome-container"
            style={styles.startButtonContainer}
          >
            {/* Render particles */}
            {particles.map((particle, index) => (
              <div
                key={index}
                style={{
                  position: "absolute",
                  width: `${particle.size}px`,
                  height: `${particle.size}px`,
                  backgroundColor: "#fff",
                  borderRadius: "50%",
                  left: `${particle.x}px`,
                  top: `${particle.y}px`,
                  opacity: particle.opacity,
                  boxShadow: "0 0 10px rgba(0, 123, 255, 0.5)",
                }}
              />
            ))}

            <div style={styles.welcomeContent}>
              <div style={styles.decorativeGrid}></div>
              <div style={styles.topAccentLine}></div>
              <div style={styles.logoMark}>DC</div>
              <h2 className="welcome-heading" style={styles.welcomeHeading}>
                INTELLIGENT ELEVATOR{" "}
                <span style={styles.accentText}>SYSTEMS</span>
              </h2>
              <p className="welcome-text" style={styles.welcomeText}>
                Combining cutting-edge technology with precision engineering for
                superior vertical mobility
              </p>
              <button
                className="start-button"
                style={styles.startButton}
                onClick={startAnimation}
              >
                <span style={styles.buttonIcon}>⟁</span> Launch Experience
              </button>
              <div style={styles.welcomeFooter}>
                <span style={styles.footerText}>DEECEE ELEVATORS</span>
                <span style={styles.footerSeparator}>•</span>
                <span style={styles.footerText}>INNOVATION IN MOTION</span>
              </div>
            </div>
          </div>
        ) : (
          <div style={styles.elevatorFrame}>
            {/* Elevator casing */}
            <div style={styles.elevatorCasing}>
              {/* Ceiling lights */}
              <div
                style={{
                  ...styles.ceilingLight,
                  opacity: lightsOn ? 1 : 0.2,
                  boxShadow: lightsOn
                    ? "0 0 60px 10px rgba(0, 123, 255, 0.5), inset 0 0 30px rgba(255, 255, 255, 0.5)"
                    : "none",
                }}
              ></div>

              {/* Corner decorations */}
              <div style={styles.cornerDecor1}></div>
              <div style={styles.cornerDecor2}></div>
              <div style={styles.cornerDecor3}></div>
              <div style={styles.cornerDecor4}></div>
            </div>

            {/* Enhanced digital floor indicator */}
            <div style={styles.digitalFloorIndicator}>
              <div style={styles.floorIndicatorFrame}></div>
              <div style={styles.floorIndicatorDisplay}>
                <div style={styles.floorDirectionIndicator}>
                  <span style={styles.directionArrowUp}>▲</span>
                  <span style={styles.directionArrowDown}>▼</span>
                </div>
                <div style={styles.floorNumberContainer}>
                  <div style={styles.floorNumber}>{floorCount}</div>
                  <div style={styles.floorNumberReflection}>{floorCount}</div>
                </div>
                <div style={styles.floorText}>FLOOR</div>
              </div>
              <div style={styles.digitalGlitch}></div>
              <div style={styles.indicatorScanline}></div>
            </div>

            {/* Elevator interior visible when doors open */}
            <div className="elevator-interior" style={styles.elevatorInterior}>
              {/* Interior wall details - Updated with blue theme design */}
              <div style={styles.interiorWall}></div>

              {/* Enhanced cityscape background visible through glass panels */}
              <div style={styles.cityscapeBackground}>
                <div style={styles.cityScape}></div>
                {/* <div style={styles.cityLights}></div> */}
                <div style={styles.skyGradient}></div>
                <div style={styles.stars}></div>
                {/* <div style={styles.flyingObjects}></div> */}
              </div>

              {/* Enhanced glass panels on sides */}
              {/* <div style={styles.glassPanel1}>
                <div style={styles.glassPanelFrame}></div>
                <div style={styles.glassPanelReflection}></div>
                <div style={styles.glassPanelHighlight}></div>
              </div>
              <div style={styles.glassPanel2}>
                <div style={styles.glassPanelFrame}></div>
                <div style={styles.glassPanelReflection}></div>
                <div style={styles.glassPanelHighlight}></div>
              </div> */}

              {/* Added decorative panel */}
              <div style={styles.decorativePanels}>
                <div style={styles.decorPanelLine}></div>
                <div style={styles.decorPanelLine}></div>
                <div style={styles.decorPanelLine}></div>
              </div>

              {/* Digital wall display */}
              <div style={styles.digitalDisplay}>
                <div style={styles.displayHeader}>WELCOME TO</div>
                <div style={styles.displayContent}>DEECEE ELEVATORS</div>
                <div style={styles.displayDataStream}></div>
              </div>

              {/* Interior panels with updated design */}
              <div style={styles.interiorPanel1}>
                <div style={styles.panelDecoration}></div>
                <div style={styles.techCircuit}></div>
              </div>
              <div style={styles.interiorPanel2}>
                <div style={styles.panelDecoration}></div>
                <div style={styles.techCircuit}></div>
              </div>

              {/* Interior rails */}
              <div style={styles.interiorRails}></div>

              {/* Enhanced floor */}
              <div style={styles.elevatorFloor}>
                <div style={styles.floorPattern}></div>
                <div style={styles.floorReflection}></div>
              </div>

              {/* {showLogo && (
                <div
                  className="logo-container"
                  style={{
                    ...styles.logoContainer,
                    padding: responsiveStyles.smallScreen
                      ? "25px 20px"
                      : "40px 60px",
                    maxWidth: responsiveStyles.smallScreen ? "280px" : "500px",
                  }}
                >
                  <div style={styles.logoAccent}></div>
                  <div style={styles.hologramEffect}></div>
                  <div style={styles.logoMark}>DC</div>
                  <h1
                    className="digital-text"
                    style={{
                      ...styles.logo,
                      fontSize: responsiveStyles.smallScreen
                        ? "2.2rem"
                        : "3.5rem",
                    }}
                  >
                    DeeCee <span style={styles.logoHighlight}>Elevators</span>
                  </h1>
                  <div
                    style={{
                      ...styles.taglineWrapper,
                      padding: responsiveStyles.smallScreen
                        ? "4px 12px"
                        : "6px 16px",
                    }}
                  >
                    <p
                      style={{
                        ...styles.tagline,
                        fontSize: responsiveStyles.smallScreen
                          ? "0.9rem"
                          : "1.3rem",
                      }}
                    >
                      Precision Engineering in Motion
                    </p>
                  </div>
                  <div style={styles.logoDecor}></div>
                </div>
              )} */}
            </div>

            {/* Updated modern control panel */}
            <div
              style={{
                ...styles.controlPanel,
                top: responsiveStyles.smallScreen ? "5%" : "25%",
                transform: responsiveStyles.smallScreen
                  ? "translateZ(10px) rotateY(0deg) scale(0.8)"
                  : "translateZ(20px) rotateY(-3deg)",
                transformOrigin: "top left",
              }}
            >
              <div style={styles.panelScreen}>
                <span style={styles.screenText}>FLOOR</span>
                <span style={styles.screenNumber}>{floorCount}</span>
              </div>

              {[1, 2, 3, 4, 5].map((num) => (
                <div
                  key={num}
                  className={
                    floorCount >= num ? "floor-button active" : "floor-button"
                  }
                  style={{
                    ...styles.floorButton,
                    backgroundColor: floorCount >= num ? "#0078ff" : "#2a2a2a",
                  }}
                >
                  {num}
                </div>
              ))}

              <div style={styles.emergencyButton}>
                <span style={styles.emergencyIcon}>!</span>
              </div>

              <div style={styles.panelScanline}></div>
            </div>

            {/* Left door with improved details */}
            <div
              style={{
                ...styles.door,
                ...styles.leftDoor,
                transform:
                  doorState === "closed"
                    ? "translateX(0) translateZ(0)"
                    : doorState === "opening" || doorState === "open"
                    ? "translateX(-100%) translateZ(0)"
                    : "translateX(0) translateZ(0)",
              }}
            >
              <div style={styles.doorPattern}></div>
              <div style={styles.doorHandleLeft}></div>
              <div style={styles.doorSeam}></div>
              <div style={styles.doorReflection}></div>
              <div style={styles.doorHighlight}></div>
            </div>

            {/* Right door with improved details */}
            <div
              style={{
                ...styles.door,
                ...styles.rightDoor,
                transform:
                  doorState === "closed"
                    ? "translateX(0) translateZ(0)"
                    : doorState === "opening" || doorState === "open"
                    ? "translateX(100%) translateZ(0)"
                    : "translateX(0) translateZ(0)",
              }}
            >
              <div style={styles.doorPattern}></div>
              <div style={styles.doorHandleRight}></div>
              <div style={styles.doorSeam}></div>
              <div style={styles.doorReflection}></div>
              <div style={styles.doorHighlight}></div>
            </div>

            {/* Elevator lights */}
            <div
              style={{
                ...styles.elevatorLight,
                opacity: lightsOn ? 0.9 : 0.1,
              }}
            ></div>
          </div>
        )}

        {/* Audio elements */}
        <audio
          ref={audioRef}
          src="/sounds/elevator-ting.mp3"
          preload="auto"
          controls={false}
          crossOrigin="anonymous"
        />
        <audio
          ref={doorSoundRef}
          src="/sounds/door-open.mp3"
          preload="auto"
          controls={false}
          crossOrigin="anonymous"
        />
      </div>
    </>
  );
};

const styles = {
  container: {
    position: "fixed",
    top: 0,
    left: 0,
    width: "100%",
    height: "100vh",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "#0a0a0a",
    zIndex: 1000,
    transition: "opacity 1s ease",
    perspective: "1200px",
  },
  startButtonContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "100%",
    width: "100%",
    background:
      "linear-gradient(135deg, #0a1018 0%, #0f1d35 50%, #162b50 100%)",
    position: "relative",
    overflow: "hidden",
  },
  welcomeContent: {
    textAlign: "center",
    padding: "60px 65px",
    borderRadius: "10px",
    backdropFilter: "blur(12px)",
    backgroundColor: "rgba(255, 255, 255, 0.02)",
    boxShadow:
      "0 30px 60px rgba(0,0,0,0.25), inset 0 1px 1px rgba(0, 123, 255, 0.05)",
    border: "1px solid rgba(0, 123, 255, 0.1)",
    maxWidth: "650px",
    position: "relative",
    overflow: "hidden",
    transformStyle: "preserve-3d",
    zIndex: 10,
  },
  decorativeGrid: {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    backgroundImage: `
      linear-gradient(rgba(0, 123, 255, 0.05) 1px, transparent 1px),
      linear-gradient(90deg, rgba(0, 123, 255, 0.05) 1px, transparent 1px)
    `,
    backgroundSize: "20px 20px",
    animation: "gridBackground 15s linear infinite",
    zIndex: -1,
  },
  topAccentLine: {
    position: "absolute",
    top: 0,
    left: "10%",
    right: "10%",
    height: "3px",
    background:
      "linear-gradient(to right, rgba(0, 123, 255, 0), rgba(0, 123, 255, 0.5), rgba(0, 123, 255, 0))",
    boxShadow: "0 0 10px rgba(0, 123, 255, 0.5)",
  },
  logoMark: {
    position: "absolute",
    top: "20px",
    left: "20px",
    fontSize: "26px",
    fontFamily: "'Montserrat', sans-serif",
    color: "rgba(0, 123, 255, 0.8)",
    fontWeight: "700",
    letterSpacing: "1px",
  },
  welcomeHeading: {
    color: "#ffffff",
    fontSize: "2.8rem",
    marginBottom: "28px",
    fontWeight: "700",
    letterSpacing: "1.5px",
    textTransform: "uppercase",
    fontFamily: "'Montserrat', sans-serif",
    lineHeight: "1.2",
    position: "relative",
    zIndex: 2,
  },
  accentText: {
    color: "#0078ff",
    display: "block",
    marginTop: "2px",
    fontWeight: "700",
  },
  welcomeText: {
    color: "#c4d8f0",
    fontSize: "1.25rem",
    marginBottom: "45px",
    lineHeight: "1.7",
    fontWeight: "300",
    maxWidth: "520px",
    margin: "0 auto 45px auto",
    fontFamily: "'Poppins', sans-serif",
  },
  startButton: {
    padding: "18px 40px",
    fontSize: "1.1rem",
    background:
      "linear-gradient(to right, rgba(0, 89, 255, 0.9), rgba(0, 123, 255, 0.9))",
    color: "white",
    border: "none",
    borderRadius: "4px",
    cursor: "pointer",
    boxShadow: "0 10px 25px rgba(0, 123, 255, 0.3)",
    transition: "all 0.3s ease",
    fontWeight: "500",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    margin: "0 auto",
    fontFamily: "'Montserrat', sans-serif",
    textTransform: "uppercase",
    letterSpacing: "1px",
  },
  buttonIcon: {
    marginRight: "12px",
    fontSize: "1.4rem",
    fontWeight: "bold",
  },
  welcomeFooter: {
    marginTop: "50px",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    color: "rgba(255,255,255,0.5)",
    fontSize: "0.9rem",
    letterSpacing: "2px",
    fontFamily: "'Montserrat', sans-serif",
  },
  footerText: {
    margin: "0 10px",
    fontWeight: "300",
  },
  footerSeparator: {
    margin: "0 8px",
    color: "rgba(0, 123, 255, 0.7)",
    fontSize: "1.2rem",
  },

  // Elevator Frame and Casing
  elevatorCasing: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "#0a0a0a",
    zIndex: 0,
    overflow: "hidden",
    boxShadow: "inset 0 0 50px rgba(0,0,0,0.5)",
  },
  cornerDecor1: {
    position: "absolute",
    top: "15px",
    right: "15px",
    width: "120px",
    height: "120px",
    borderTop: "3px solid rgba(0, 123, 255, 0.1)",
    borderRight: "3px solid rgba(0, 123, 255, 0.1)",
  },
  cornerDecor2: {
    position: "absolute",
    bottom: "15px",
    left: "15px",
    width: "120px",
    height: "120px",
    borderBottom: "3px solid rgba(0, 123, 255, 0.1)",
    borderLeft: "3px solid rgba(0, 123, 255, 0.1)",
  },
  elevatorFrame: {
    position: "relative",
    width: "100%",
    height: "100vh",
    backgroundColor: "#0a0a0a",
    overflow: "hidden",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    transformStyle: "preserve-3d",
  },

  cornerDecor3: {
    position: "absolute",
    top: "15px",
    left: "15px",
    width: "120px",
    height: "120px",
    borderTop: "3px solid rgba(0, 123, 255, 0.1)",
    borderLeft: "3px solid rgba(0, 123, 255, 0.1)",
  },
  cornerDecor4: {
    position: "absolute",
    bottom: "15px",
    right: "15px",
    width: "120px",
    height: "120px",
    borderBottom: "3px solid rgba(0, 123, 255, 0.1)",
    borderRight: "3px solid rgba(0, 123, 255, 0.1)",
  },

  // Digital Floor Indicator
  digitalFloorIndicator: {
    position: "absolute",
    top: "5%",
    right: "5%",
    width: "150px",
    height: "180px",
    backgroundColor: "#050a14",
    color: "#0078ff",
    borderRadius: "6px",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    fontFamily: "'Montserrat', sans-serif",
    zIndex: 15,
    boxShadow:
      "0 0 20px rgba(0, 123, 255, 0.4), inset 0 0 15px rgba(0, 123, 255, 0.1)",
    border: "1px solid #1a3a6c",
    overflow: "hidden",
    transform: "translateZ(15px)",
    transformStyle: "preserve-3d",
  },
  floorIndicatorFrame: {
    position: "absolute",
    top: "3px",
    left: "3px",
    right: "3px",
    bottom: "3px",
    border: "1px solid rgba(0, 89, 255, 0.3)",
    borderRadius: "4px",
    pointerEvents: "none",
  },
  floorIndicatorDisplay: {
    width: "90%",
    height: "90%",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    position: "relative",
  },
  floorDirectionIndicator: {
    position: "absolute",
    top: "15px",
    left: "15px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },
  directionArrowUp: {
    color: "#0078ff",
    fontSize: "1rem",
    textShadow: "0 0 5px rgba(0, 123, 255, 0.8)",
    opacity: 0.9,
    marginBottom: "2px",
  },
  directionArrowDown: {
    color: "#505865",
    fontSize: "1rem",
    opacity: 0.6,
  },
  floorNumberContainer: {
    position: "relative",
    marginBottom: "10px",
  },
  floorNumber: {
    fontSize: "4.5rem",
    fontWeight: "bold",
    color: "#0078ff",
    textShadow: "0 0 15px rgba(0, 123, 255, 0.7)",
    lineHeight: "1",
    letterSpacing: "-2px",
  },
  floorNumberReflection: {
    fontSize: "4.5rem",
    fontWeight: "bold",
    color: "rgba(0, 120, 255, 0.2)",
    transform: "scaleY(-0.25) translateY(-5px)",
    lineHeight: "0.5",
    letterSpacing: "-2px",
    filter: "blur(1px)",
  },
  floorText: {
    fontSize: "0.9rem",
    fontWeight: "500",
    color: "#ffffff",
    letterSpacing: "2px",
    margin: "5px 0",
    textShadow: "0 0 5px rgba(255, 255, 255, 0.3)",
  },
  elevatorStatus: {
    fontSize: "0.7rem",
    color: "#8fcefd",
    letterSpacing: "1px",
    padding: "4px 10px",
    marginTop: "15px",
    border: "1px solid rgba(0, 123, 255, 0.2)",
    borderRadius: "3px",
    background: "rgba(0, 123, 255, 0.05)",
  },
  digitalGlitch: {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    background: `
      linear-gradient(90deg, 
        rgba(0, 123, 255, 0) 0%, 
        rgba(0, 123, 255, 0) 97%, 
        rgba(0, 123, 255, 0.5) 98%, 
        rgba(0, 123, 255, 0) 99%,
        rgba(0, 123, 255, 0) 100%)
    `,
    opacity: 0.6,
    backgroundSize: "300% 100%",
    animation: "scanline 3s linear infinite",
    pointerEvents: "none",
    zIndex: 3,
  },
  indicatorScanline: {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "5px",
    background:
      "linear-gradient(to bottom, rgba(0, 123, 255, 0.3), rgba(0, 123, 255, 0))",
    animation: "scanline 2s linear infinite",
    zIndex: 4,
  },

  // Enhanced Cityscape Background
  cityscapeBackground: {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    background: "linear-gradient(to bottom, #040a18 0%, #0a1725 100%)",
    overflow: "hidden",
    zIndex: 1,
  },
  cityScape: {
    position: "absolute",
    bottom: 0,
    left: 0,
    width: "100%",
    height: "50%",
    backgroundImage: `url("data:image/svg+xml,%3Csvg width='1200' height='400' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M0,400 L0,280 L30,280 L30,250 L60,250 L60,290 L90,290 L90,260 L120,260 L120,240 L150,240 L150,200 L180,200 L180,220 L210,220 L210,180 L240,180 L240,200 L270,200 L270,160 L300,160 L300,190 L330,190 L330,150 L360,150 L360,170 L390,170 L390,130 L420,130 L420,170 L450,170 L450,110 L480,110 L480,140 L510,140 L510,80 L540,80 L540,120 L570,120 L570,160 L600,160 L600,120 L630,120 L630,100 L660,100 L660,150 L690,150 L690,90 L720,90 L720,130 L750,130 L750,170 L780,170 L780,130 L810,130 L810,190 L840,190 L840,150 L870,150 L870,190 L900,190 L900,240 L930,240 L930,200 L960,200 L960,290 L990,290 L990,250 L1020,250 L1020,300 L1050,300 L1050,260 L1080,260 L1080,290 L1110,290 L1110,270 L1140,270 L1140,300 L1170,300 L1170,250 L1200,250 L1200,400 Z' fill='%23051025' /%3E%3C/svg%3E")`,
    backgroundSize: "cover",
    backgroundPosition: "center bottom",
    opacity: 0.8,
  },
  cityLights: {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    backgroundImage: `
      radial-gradient(circle, rgba(0, 123, 255, 0.8) 1px, transparent 1px),
      radial-gradient(circle, rgba(255, 255, 255, 0.6) 1px, transparent 1px),
      radial-gradient(circle, rgba(0, 89, 255, 0.6) 1px, transparent 1px)
    `,
    backgroundSize: "80px 80px, 50px 50px, 30px 30px",
    backgroundPosition: "0 0, 25px 25px, 10px 15px",
    animation: "cityLights 20s linear infinite",
    opacity: 0.9,
  },
  skyGradient: {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "60%",
    background:
      "linear-gradient(to bottom, rgba(10, 30, 60, 0.6) 0%, rgba(5, 15, 35, 0) 100%)",
  },
  stars: {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "70%",
    backgroundImage: `
      radial-gradient(circle, rgba(255, 255, 255, 0.9) 1px, transparent 1px),
      radial-gradient(circle, rgba(255, 255, 255, 0.7) 1px, transparent 1px),
      radial-gradient(circle, rgba(255, 255, 255, 0.5) 1px, transparent 1px)
    `,
    backgroundSize: "300px 300px, 200px 200px, 100px 100px",
    opacity: 0.7,
  },
  flyingObjects: {
    position: "absolute",
    top: "15%",
    left: 0,
    width: "100%",
    height: "5px",
    backgroundImage: `
    radial-gradient(circle, rgba(0, 123, 255, 1) 1px, transparent 1px),
    linear-gradient(90deg, rgba(0, 123, 255, 0.7) 3px, transparent 0)
  `,
    backgroundSize: "200px 100px, 40px 2px",
    backgroundPosition: "0 0, 0 10px",
    animation: "cityLights 15s linear infinite reverse",
  },

  // Enhanced Glass Panels
  glassPanel1: {
    position: "absolute",
    top: "15%",
    left: "12%",
    width: "35%",
    height: "50%",
    background:
      "linear-gradient(135deg, rgba(255, 255, 255, 0.05) 0%, rgba(255, 255, 255, 0.02) 100%)",
    borderRadius: "4px",
    border: "1px solid rgba(255, 255, 255, 0.1)",
    boxShadow: "inset 0 0 20px rgba(0, 123, 255, 0.1)",
    backdropFilter: "blur(2px)",
    zIndex: 2,
    overflow: "hidden",
  },
  glassPanelFrame: {
    position: "absolute",
    top: "10px",
    left: "10px",
    right: "10px",
    bottom: "10px",
    border: "1px solid rgba(255, 255, 255, 0.1)",
    borderRadius: "2px",
  },
  glassPanelReflection: {
    position: "absolute",
    top: 0,
    left: "-100%",
    width: "300%",
    height: "100%",
    background:
      "linear-gradient(90deg, rgba(0, 123, 255, 0) 0%, rgba(0, 123, 255, 0.05) 50%, rgba(0, 123, 255, 0) 100%)",
    transform: "rotate(-45deg)",
    animation: "doorShine 8s infinite linear",
  },
  glassPanelHighlight: {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    background:
      "linear-gradient(135deg, rgba(0, 123, 255, 0.03) 0%, rgba(0, 0, 0, 0) 50%)",
  },
  glassPanel2: {
    position: "absolute",
    top: "15%",
    right: "12%",
    width: "35%",
    height: "50%",
    background:
      "linear-gradient(135deg, rgba(255, 255, 255, 0.05) 0%, rgba(255, 255, 255, 0.02) 100%)",
    borderRadius: "4px",
    border: "1px solid rgba(255, 255, 255, 0.1)",
    boxShadow: "inset 0 0 20px rgba(0, 123, 255, 0.1)",
    backdropFilter: "blur(2px)",
    zIndex: 2,
    overflow: "hidden",
  },

  // Enhanced Interior Elements
  techCircuit: {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M0,50 L30,50 L30,30 L60,30 L60,60 L40,60 L40,100' stroke='%230078ff' stroke-opacity='0.15' stroke-width='1' fill='none'/%3E%3Cpath d='M100,20 L70,20 L70,40 L40,40 L40,10 L60,10 L60,0' stroke='%230078ff' stroke-opacity='0.1' stroke-width='1' fill='none'/%3E%3Ccircle cx='30' cy='50' r='2' fill='%230078ff' fill-opacity='0.15'/%3E%3Ccircle cx='60' cy='30' r='2' fill='%230078ff' fill-opacity='0.15'/%3E%3Ccircle cx='40' cy='60' r='2' fill='%230078ff' fill-opacity='0.15'/%3E%3Ccircle cx='70' cy='20' r='2' fill='%230078ff' fill-opacity='0.15'/%3E%3Ccircle cx='40' cy='40' r='2' fill='%230078ff' fill-opacity='0.15'/%3E%3Ccircle cx='60' cy='10' r='2' fill='%230078ff' fill-opacity='0.15'/%3E%3C/svg%3E")`,
    opacity: 0.6,
  },
  floorReflection: {
    position: "absolute",
    bottom: 0,
    left: 0,
    width: "100%",
    height: "100%",
    background:
      "linear-gradient(to top, rgba(0, 123, 255, 0.1), rgba(0, 123, 255, 0))",
    opacity: 0.5,
  },
  floorDirection: {
    margin: "5px 0",
  },
  directionArrow: {
    color: "#0078ff",
    fontSize: "1.2rem",
    textShadow: "0 0 5px rgba(0, 123, 255, 0.5)",
  },
  indicatorScanline: {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "10px",
    background:
      "linear-gradient(to bottom, rgba(0, 123, 255, 0.2), rgba(0, 123, 255, 0))",
    animation: "scanline 2s linear infinite",
    zIndex: 2,
  },

  // Elevator Interior
  elevatorInterior: {
    position: "absolute",
    width: "100%",
    height: "100%",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    flexDirection: "column",
    perspective: "1000px",
    transformStyle: "preserve-3d",
  },
  interiorWall: {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    background: "linear-gradient(to bottom, #0f1725, #0a111d)",
    boxShadow: "inset 0 0 50px rgba(0,0,0,0.5)",
    overflow: "hidden",
  },

  // City background behind glass
  cityscapeBackground: {
    position: "absolute",
    top: "10%",
    left: "10%",
    width: "80%",
    height: "60%",
    background: "linear-gradient(to bottom, #05070d 0%, #0a1725 100%)",
    borderRadius: "5px",
    overflow: "hidden",
    opacity: 0.7,
    zIndex: 1,
  },
  cityLights: {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    backgroundImage: `
      radial-gradient(circle, #0078ff 1px, transparent 1px),
      radial-gradient(circle, #ffffff 1px, transparent 1px)
    `,
    backgroundSize: "50px 50px, 30px 30px",
    backgroundPosition: "0 0, 25px 25px",
    animation: "cityLights 20s linear infinite",
  },

  // Glass panels
  glassPanel1: {
    position: "absolute",
    top: "15%",
    left: "12%",
    width: "35%",
    height: "50%",
    background:
      "linear-gradient(135deg, rgba(255, 255, 255, 0.05) 0%, rgba(255, 255, 255, 0.02) 100%)",
    borderRadius: "4px",
    border: "1px solid rgba(255, 255, 255, 0.1)",
    boxShadow: "inset 0 0 20px rgba(0, 123, 255, 0.1)",
    backdropFilter: "blur(2px)",
    zIndex: 2,
  },
  glassPanel2: {
    position: "absolute",
    top: "15%",
    right: "12%",
    width: "35%",
    height: "50%",
    background:
      "linear-gradient(135deg, rgba(255, 255, 255, 0.05) 0%, rgba(255, 255, 255, 0.02) 100%)",
    borderRadius: "4px",
    border: "1px solid rgba(255, 255, 255, 0.1)",
    boxShadow: "inset 0 0 20px rgba(0, 123, 255, 0.1)",
    backdropFilter: "blur(2px)",
    zIndex: 2,
  },

  // Digital display
  digitalDisplay: {
    position: "absolute",
    top: "35%",
    left: "32%",
    width: "36%",
    height: "15%",
    background: "#0a0e1a",
    borderRadius: "2px",
    border: "1px solid rgba(0, 123, 255, 0.3)",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    boxShadow: "0 0 15px rgba(0, 123, 255, 0.2)",
    zIndex: 3,
  },
  displayHeader: {
    fontSize: "0.8rem",
    color: "#ffffff",
    fontFamily: "'Montserrat', sans-serif",
    letterSpacing: "1px",
    fontWeight: "300",
  },
  displayContent: {
    fontSize: "1.5rem",
    color: "#0078ff",
    fontFamily: "'Montserrat', sans-serif",
    fontWeight: "700",
    letterSpacing: "1px",
    margin: "5px 0",
    textShadow: "0 0 8px rgba(0, 123, 255, 0.5)",
  },
  displayDataStream: {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    background: `
      linear-gradient(0deg, 
        rgba(0, 123, 255, 0) 40%, 
        rgba(0, 123, 255, 0.1) 50%, 
        rgba(0, 123, 255, 0) 60%)
    `,
    backgroundSize: "100% 200%",
    animation: "dataStream 2s linear infinite",
    pointerEvents: "none",
  },

  decorativePanels: {
    position: "absolute",
    bottom: "10%",
    left: "5%",
    width: "90%",
    height: "1px",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-around",
    alignItems: "center",
    zIndex: 2,
  },
  decorPanelLine: {
    width: "100%",
    height: "1px",
    background:
      "linear-gradient(to right, rgba(0, 123, 255, 0), rgba(0, 123, 255, 0.5), rgba(0, 123, 255, 0))",
    boxShadow: "0 0 8px rgba(0, 123, 255, 0.4)",
    marginBottom: "20px",
  },
  interiorPanel1: {
    position: "absolute",
    top: "35%",
    left: "5%",
    width: "20%",
    height: "35%",
    background: "linear-gradient(to bottom, #0c1424, #070b16)",
    borderRadius: "2px",
    boxShadow: "inset 0 0 10px rgba(0,0,0,0.2)",
    border: "1px solid rgba(0, 123, 255, 0.15)",
    zIndex: 2,
    overflow: "hidden",
  },
  interiorPanel2: {
    position: "absolute",
    bottom: "20%",
    right: "5%",
    width: "20%",
    height: "25%",
    background: "linear-gradient(to right, #0c1424, #070b16)",
    borderRadius: "2px",
    boxShadow: "inset 0 0 10px rgba(0,0,0,0.2)",
    border: "1px solid rgba(0, 123, 255, 0.15)",
    zIndex: 2,
    overflow: "hidden",
  },
  panelDecoration: {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    backgroundImage: `url("data:image/svg+xml,%3Csvg width='20' height='20' viewBox='0 0 20 20' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%230078ff' fill-opacity='0.1' fill-rule='evenodd'%3E%3Ccircle cx='3' cy='3' r='3'/%3E%3Ccircle cx='13' cy='13' r='3'/%3E%3C/g%3E%3C/svg%3E")`,
  },
  interiorRails: {
    position: "absolute",
    bottom: "5%",
    left: 0,
    width: "100%",
    height: "3px",
    background:
      "linear-gradient(to right, rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0.3), rgba(255, 255, 255, 0.1))",
    boxShadow: "0 0 5px rgba(0, 123, 255, 0.5)",
    zIndex: 3,
  },
  elevatorFloor: {
    position: "absolute",
    bottom: 0,
    left: 0,
    width: "100%",
    height: "5%",
    background: "linear-gradient(to right, #0c1424, #0f1930, #0c1424)",
    boxShadow: "inset 0 2px 5px rgba(0,0,0,0.5)",
    overflow: "hidden",
  },
  floorPattern: {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    backgroundImage:
      "repeating-linear-gradient(90deg, rgba(0, 123, 255, 0.05), rgba(0, 123, 255, 0.05) 2px, transparent 2px, transparent 20px)",
  },
  ceilingLight: {
    position: "absolute",
    top: "5%",
    left: "50%",
    width: "60%",
    height: "10px",
    backgroundColor: "rgba(0, 123, 255, 0.1)",
    transform: "translateX(-50%)",
    borderRadius: "20px",
    transition: "all 0.8s ease",
    zIndex: 2,
  },

  // Door styling
  door: {
    position: "absolute",
    top: 0,
    height: "100%",
    width: "50%",
    background:
      "linear-gradient(135deg, #1a1a1a 0%, #0c0c0c 40%, #1a1a1a 60%, #0c0c0c 100%)",
    transition: "transform 1.5s cubic-bezier(0.65, 0, 0.35, 1)",
    zIndex: 10,
    boxShadow: "0 0 15px rgba(0,0,0,0.5)",
    transformStyle: "preserve-3d",
    borderTop: "1px solid rgba(255,255,255,0.1)",
    borderBottom: "1px solid rgba(0,0,0,0.8)",
    overflow: "hidden",
  },
  leftDoor: {
    left: 0,
    borderRight: "1px solid rgba(255,255,255,0.07)",
  },
  rightDoor: {
    right: 0,
    borderLeft: "1px solid rgba(255,255,255,0.07)",
  },
  doorPattern: {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    backgroundImage: `
      linear-gradient(0deg, rgba(255,255,255,0.02) 1px, transparent 1px),
      linear-gradient(90deg, rgba(255,255,255,0.02) 1px, transparent 1px)
    `,
    backgroundSize: "20px 20px",
  },
  doorHandleRight: {
    position: "absolute",
    top: "50%",
    width: "6px",
    height: "70px",
    background: "linear-gradient(to right, #1a1a1a, #2a2a2a, #1a1a1a)",
    borderRadius: "3px",
    transform: "translateY(-50%)",
    left: "15px",
    boxShadow: "0 0 5px rgba(0,0,0,0.6), inset 0 0 3px rgba(255,255,255,0.1)",
    border: "1px solid rgba(0,0,0,0.3)",
  },
  doorHandleLeft: {
    position: "absolute",
    top: "50%",
    width: "6px",
    height: "70px",
    background: "linear-gradient(to right, #1a1a1a, #2a2a2a, #1a1a1a)",
    borderRadius: "3px",
    transform: "translateY(-50%)",
    right: "15px",
    boxShadow: "0 0 5px rgba(0,0,0,0.6), inset 0 0 3px rgba(255,255,255,0.1)",
    border: "1px solid rgba(0,0,0,0.3)",
  },
  doorSeam: {
    position: "absolute",
    top: 0,
    bottom: 0,
    width: "1px",
    background:
      "linear-gradient(to bottom, rgba(0, 123, 255, 0.1), rgba(0, 123, 255, 0.05), rgba(0, 123, 255, 0.15))",
    boxShadow: "0 0 3px rgba(0, 123, 255, 0.1)",
    right: "30%",
  },
  doorReflection: {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    background: `
      linear-gradient(135deg, rgba(255,255,255,0.07) 0%, rgba(255,255,255,0) 20%),
      linear-gradient(to right, rgba(255,255,255,0.03) 0%, rgba(255,255,255,0.05) 10%, rgba(255,255,255,0) 30%),
      linear-gradient(to bottom, rgba(255,255,255,0.07) 0%, rgba(255,255,255,0) 40%)
    `,
    pointerEvents: "none",
    zIndex: 2,
  },
  doorHighlight: {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    backgroundSize: "200% 100%",
    backgroundImage: `
      linear-gradient(
        90deg,
        rgba(0, 123, 255,0) 0%,
        rgba(0, 123, 255,0) 35%,
        rgba(0, 123, 255,0.05) 50%,
        rgba(0, 123, 255,0) 65%,
        rgba(0, 123, 255,0) 100%
      )
    `,
    animation: "doorShine 4s infinite linear",
    pointerEvents: "none",
    zIndex: 3,
  },

  // Modern control panel
  controlPanel: {
    position: "absolute",
    top: "25%",
    left: "5%",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    gap: "10px",
    backgroundColor: "#0a1018",
    padding: "20px 15px",
    borderRadius: "4px",
    zIndex: 15,
    boxShadow: "0 5px 15px rgba(0,0,0,0.5), 0 0 10px rgba(0, 123, 255, 0.2)",
    border: "1px solid #0c1a2e",
    transform: "translateZ(20px) rotateY(-3deg)",
    transformStyle: "preserve-3d",
    overflow: "hidden",
    // Responsive positioning handled through props
  },
  panelScanline: {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    background:
      "linear-gradient(to bottom, rgba(0, 123, 255, 0), rgba(0, 123, 255, 0.03) 50%, rgba(0, 123, 255, 0))",
    animation: "scanline 4s linear infinite",
    pointerEvents: "none",
  },
  panelScreen: {
    width: "100%",
    padding: "10px 5px",
    backgroundColor: "#050a12",
    color: "#0078ff",
    borderRadius: "3px",
    textAlign: "center",
    marginBottom: "15px",
    boxShadow: "inset 0 0 5px rgba(0, 123, 255, 0.2)",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    border: "1px solid #0c1a2e",
  },
  screenText: {
    fontSize: "0.8rem",
    opacity: 0.7,
    marginBottom: "5px",
    fontFamily: "'Montserrat', sans-serif",
    letterSpacing: "1px",
  },
  screenNumber: {
    fontSize: "1.8rem",
    fontWeight: "bold",
    textShadow: "0 0 5px rgba(0, 123, 255, 0.5)",
    fontFamily: "'Montserrat', sans-serif",
  },
  floorButton: {
    width: "40px",
    height: "40px",
    borderRadius: "3px",
    display: "flex",
    justifyContent: "center",
    marginLeft: "10px",
    alignItems: "center",
    fontWeight: "bold",
    color: "white",
    cursor: "pointer",
    boxShadow: "inset 0 0 5px rgba(0,0,0,0.5), 0 2px 5px rgba(0,0,0,0.3)",
    border: "1px solid #0c1a2e",
    transition: "background-color 0.3s ease",
    transform: "translateZ(5px)",
    fontFamily: "'Montserrat', sans-serif",
  },
  emergencyButton: {
    width: "40px",
    height: "40px",
    marginLeft: "10px",
    borderRadius: "3px",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "#d10000",
    color: "white",
    marginTop: "15px",
    boxShadow:
      "0 0 10px rgba(209, 0, 0, 0.3), inset 0 0 5px rgba(255,255,255,0.2)",
    border: "1px solid #7a0000",
    transform: "translateZ(5px)",
  },
  emergencyIcon: {
    fontWeight: "bold",
    fontSize: "1.2rem",
  },
  elevatorLight: {
    position: "absolute",
    top: "8%",
    left: "50%",
    width: "200px",
    height: "200px",
    borderRadius: "50%",
    background:
      "radial-gradient(circle, rgba(0, 123, 255, 0.4) 0%, rgba(0, 123, 255, 0.1) 50%, rgba(0, 123, 255, 0) 70%)",
    transform: "translate(-50%, -30%)",
    opacity: 0.7,
    pointerEvents: "none",
    zIndex: 5,
    transition: "opacity 0.8s ease",
  },

  // Logo container
  logoContainer: {
    zIndex: 5,
    textAlign: "center",
    width: "80%",
    maxWidth: "500px",
    background:
      "linear-gradient(145deg, rgba(13, 21, 37, 0.95), rgba(7, 13, 25, 0.95))",
    padding: "40px 60px",
    borderRadius: "8px",
    boxShadow:
      "0 20px 50px rgba(0,0,0,0.4), 0 0 40px rgba(0, 123, 255, 0.15), inset 0 1px 1px rgba(255,255,255,0.05)",
    opacity: 0,
    transform: "translateZ(30px)",
    transformStyle: "preserve-3d",
    border: "1px solid rgba(0, 123, 255, 0.15)",
    position: "relative",
    overflow: "hidden",
  },
  logoAccent: {
    position: "absolute",
    top: "0",
    left: "0",
    right: "0",
    height: "3px",
    background:
      "linear-gradient(to right, rgba(0, 89, 255, 0.5), rgba(0, 123, 255, 0.5))",
    boxShadow: "0 0 15px rgba(0, 123, 255, 0.5)",
  },
  hologramEffect: {
    position: "absolute",
    top: "-50%",
    left: "-50%",
    width: "200%",
    height: "200%",
    background:
      "radial-gradient(ellipse at center, rgba(0, 123, 255, 0.05) 0%, rgba(0, 123, 255, 0) 70%)",
    animation: "panelGlow 4s infinite ease-in-out",
    pointerEvents: "none",
  },
  logoMark: {
    fontFamily: "'Montserrat', sans-serif",
    fontSize: "36px",
    fontWeight: "700",
    color: "rgba(0, 123, 255, 0.15)",
    position: "absolute",
    top: "20px",
    right: "25px",
    letterSpacing: "2px",
  },
  logo: {
    fontSize: "3.5rem",
    fontWeight: "700",
    fontFamily: "'Montserrat', sans-serif",
    margin: 0,
    marginBottom: "15px",
    color: "#ffffff",
    letterSpacing: "2px",
    textTransform: "uppercase",
    position: "relative",
    display: "inline-block",
    textShadow: "0 0 15px rgba(0, 123, 255, 0.3)",
  },
  logoHighlight: {
    color: "#0078ff",
    position: "relative",
    display: "inline-block",
  },
  taglineWrapper: {
    background: "rgba(0, 123, 255, 0.03)",
    padding: "6px 16px",
    borderRadius: "30px",
    display: "inline-block",
    marginTop: "5px",
    border: "1px solid rgba(0, 123, 255, 0.1)",
  },
  tagline: {
    fontSize: "1.3rem",
    margin: 0,
    color: "#e0e0e0",
    fontWeight: "300",
    letterSpacing: "1.5px",
    fontFamily: "'Montserrat', sans-serif",
  },
  logoDecor: {
    position: "absolute",
    bottom: "20px",
    left: "25px",
    width: "40px",
    height: "40px",
    borderLeft: "2px solid rgba(0, 123, 255, 0.15)",
    borderBottom: "2px solid rgba(0, 123, 255, 0.15)",
  },
};

export default ElevatorAnimation;
